import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Trash2,
  GripVertical,
  Plus,
  Lock,
  LogOut,
  Eye,
  ChevronUp,
  ChevronDown,
  CalendarDays,
  PartyPopper,
  Sparkles,
  Video as VideoIcon,
  Users,
  CalendarHeart,
} from 'lucide-react';
import { Settings as SettingsIcon, Tv } from 'lucide-react';
import ArtistsManager from './admin/ArtistsManager';
import EventsManager from './admin/EventsManager';
import SettingsManager from './admin/SettingsManager';
import ShowsManager from './admin/ShowsManager';

const SLOTS = ['week', 'weekend', 'special', 'mon', 'tue', 'wed', 'thu', 'fri'];

const SLOT_LABELS = {
  week: 'Semaine (général)',
  weekend: 'Week-end',
  special: 'Spécial Dimanche',
  mon: 'Lundi',
  tue: 'Mardi',
  wed: 'Mercredi',
  thu: 'Jeudi',
  fri: 'Vendredi',
};

const SLOT_BADGE_CLASS = {
  week: 'schedule-badge schedule-badge-week',
  weekend: 'schedule-badge schedule-badge-weekend',
  special: 'schedule-badge schedule-badge-special',
  mon: 'schedule-badge schedule-badge-day',
  tue: 'schedule-badge schedule-badge-day',
  wed: 'schedule-badge schedule-badge-day',
  thu: 'schedule-badge schedule-badge-day',
  fri: 'schedule-badge schedule-badge-day',
};

const TAB_ICON = {
  week: <CalendarDays size={16} />,
  weekend: <PartyPopper size={16} />,
  special: <Sparkles size={16} />,
  mon: <CalendarDays size={14} />,
  tue: <CalendarDays size={14} />,
  wed: <CalendarDays size={14} />,
  thu: <CalendarDays size={14} />,
  fri: <CalendarDays size={14} />,
};

const TAB_DESCRIPTION = {
  week: {
    title: 'Programmation Semaine — Liste générale (repli)',
    subtitle: 'Utilisée du lundi au vendredi si aucune liste spécifique au jour n\'est définie',
  },
  weekend: {
    title: 'Programmation Week-end — Samedi et Dimanche',
    subtitle: 'Diffusée le samedi et le dimanche (hors plage spéciale)',
  },
  special: {
    title: 'Programmation Spéciale — Dimanche 10h00–20h00',
    subtitle: 'Diffusée chaque dimanche de 10h à 20h (heure de Paris)',
  },
  mon: { title: 'Programmation du Lundi', subtitle: 'Diffusée uniquement le lundi. Sinon, repli sur la liste générale.' },
  tue: { title: 'Programmation du Mardi', subtitle: 'Diffusée uniquement le mardi. Sinon, repli sur la liste générale.' },
  wed: { title: 'Programmation du Mercredi', subtitle: 'Diffusée uniquement le mercredi. Sinon, repli sur la liste générale.' },
  thu: { title: 'Programmation du Jeudi', subtitle: 'Diffusée uniquement le jeudi. Sinon, repli sur la liste générale.' },
  fri: { title: 'Programmation du Vendredi', subtitle: 'Diffusée uniquement le vendredi. Sinon, repli sur la liste générale.' },
};

const WEEKDAY_SLOTS = ['mon', 'tue', 'wed', 'thu', 'fri'];
const MAIN_TOP_SLOTS = ['week', 'weekend', 'special'];

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(null);
  const [activeTab, setActiveTab] = useState('week');
  const [videosByTab, setVideosByTab] = useState({
    week: [], weekend: [], special: [],
    mon: [], tue: [], wed: [], thu: [], fri: [],
  });
  const [newVideo, setNewVideo] = useState({
    title: '',
    youtube_url: '',
    schedules: ['week', 'weekend'],
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
      fetchAllVideos();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      const { token } = response.data;

      setToken(token);
      setIsAuthenticated(true);
      localStorage.setItem('admin_token', token);
      toast.success('Connexion réussie !');
      fetchAllVideos();
    } catch (error) {
      toast.error('Mot de passe invalide');
    } finally {
      setIsLoading(false);
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setToken(null);
    localStorage.removeItem('admin_token');
    setVideosByTab({
      week: [], weekend: [], special: [],
      mon: [], tue: [], wed: [], thu: [], fri: [],
    });
    toast.info('Déconnexion réussie');
  };

  const fetchAllVideos = async () => {
    try {
      const results = await Promise.all(
        SLOTS.map((slot) => axios.get(`${API}/videos?schedule=${slot}`).then((r) => r.data || [])),
      );
      const next = {};
      SLOTS.forEach((slot, idx) => {
        next[slot] = results[idx];
      });
      setVideosByTab(next);
    } catch (error) {
      toast.error('Échec du chargement des vidéos');
    }
  };

  const toggleNewVideoSchedule = (slot) => {
    setNewVideo((prev) => {
      const has = prev.schedules.includes(slot);
      const next = has
        ? prev.schedules.filter((s) => s !== slot)
        : [...prev.schedules, slot];
      return { ...prev, schedules: next };
    });
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();

    if (!newVideo.title || !newVideo.youtube_url) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (newVideo.schedules.length === 0) {
      toast.error('Sélectionnez au moins une programmation');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(
        `${API}/videos`,
        newVideo,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Vidéo ajoutée avec succès !');
      setNewVideo({ title: '', youtube_url: '', schedules: ['week', 'weekend'] });
      fetchAllVideos();
    } catch (error) {
      toast.error("Échec de l'ajout de la vidéo");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Supprimer définitivement cette vidéo ?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/videos/${videoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Vidéo supprimée');
      fetchAllVideos();
    } catch (error) {
      toast.error('Échec de la suppression');
    }
  };

  const handleToggleSchedule = async (video, slot) => {
    const has = (video.schedules || []).includes(slot);
    const next = has
      ? video.schedules.filter((s) => s !== slot)
      : [...(video.schedules || []), slot];

    if (next.length === 0) {
      toast.error('Une vidéo doit appartenir à au moins une programmation');
      return;
    }

    try {
      await axios.put(
        `${API}/videos/${video.id}`,
        { schedules: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Programmations mises à jour');
      fetchAllVideos();
    } catch (error) {
      toast.error('Échec de la mise à jour');
    }
  };

  const handleReorder = async (tab, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    const reorderedVideos = [...videosByTab[tab]];
    const [movedVideo] = reorderedVideos.splice(fromIndex, 1);
    reorderedVideos.splice(toIndex, 0, movedVideo);

    setVideosByTab({ ...videosByTab, [tab]: reorderedVideos });

    try {
      await axios.put(
        `${API}/videos/reorder`,
        { video_ids: reorderedVideos.map((v) => v.id), schedule: tab },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ordre mis à jour');
      fetchAllVideos();
    } catch (error) {
      console.error('Reorder error:', error.response?.data || error.message);
      toast.error(`Échec de la réorganisation: ${error.response?.data?.detail || error.message}`);
      fetchAllVideos();
    }
  };

  const handleMoveUp = (tab, index) => {
    if (index === 0) return;
    handleReorder(tab, index, index - 1);
  };

  const handleMoveDown = (tab, index) => {
    if (index === videosByTab[tab].length - 1) return;
    handleReorder(tab, index, index + 1);
  };

  const renderVideoList = (tab) => {
    const videos = videosByTab[tab];
    if (videos.length === 0) {
      return (
        <div className="empty-state" data-testid={`empty-state-${tab}`}>
          <p>Aucune vidéo dans cette programmation. Ajoutez-en ci-dessus !</p>
        </div>
      );
    }

    return (
      <div className="videos-list">
        {videos.map((video, index) => (
          <div
            key={video.id}
            className="video-item"
            data-testid={`video-item-${tab}-${index}`}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', index);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e) => {
              e.preventDefault();
              const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
              handleReorder(tab, fromIndex, index);
            }}
          >
            <div className="video-drag">
              <GripVertical size={20} className="drag-icon" />
            </div>
            <div className="video-order-number">{index + 1}</div>
            <div className="video-details">
              <h4>
                {video.title}
                {(video.schedules || []).map((s) => (
                  <span
                    key={s}
                    className={SLOT_BADGE_CLASS[s]}
                    data-testid={`schedule-badge-${tab}-${index}-${s}`}
                  >
                    {SLOT_LABELS[s]}
                  </span>
                ))}
              </h4>
              <p>{video.youtube_url}</p>
            </div>
            <div className="video-actions">
              <div className="video-schedules-toggles">
                {SLOTS.map((slot) => (
                  <label
                    key={slot}
                    className="schedule-toggle"
                    data-testid={`schedule-toggle-${tab}-${index}-${slot}`}
                  >
                    <Checkbox
                      checked={(video.schedules || []).includes(slot)}
                      onCheckedChange={() => handleToggleSchedule(video, slot)}
                    />
                    <span>{SLOT_LABELS[slot]}</span>
                  </label>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleMoveUp(tab, index)}
                disabled={index === 0}
                data-testid={`move-up-${tab}-${index}`}
                className="move-button"
              >
                <ChevronUp size={20} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleMoveDown(tab, index)}
                disabled={index === videos.length - 1}
                data-testid={`move-down-${tab}-${index}`}
                className="move-button"
              >
                <ChevronDown size={20} />
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDeleteVideo(video.id)}
                data-testid={`delete-video-${tab}-${index}`}
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login" data-testid="admin-login">
        <Card className="login-card">
          <div className="login-header">
            <img
              src="https://customer-assets.emergentagent.com/job_bcc12bb2-d1c3-4c30-aab1-6d93b6ded326/artifacts/auol33u9_logo_4x.png"
              alt="Grace TV Gospel Logo"
              className="login-logo"
            />
            <h2>Accès Administrateur</h2>
            <p>Entrez le mot de passe pour gérer les vidéos</p>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            <div className="input-group">
              <Lock size={20} className="input-icon" />
              <Input
                type="password"
                placeholder="Mot de passe administrateur"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                data-testid="admin-password-input"
                className="password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading || !password}
              data-testid="admin-login-button"
              className="login-button"
            >
              {isLoading ? 'Connexion...' : 'Accéder au panneau'}
            </Button>
          </form>

          <Button
            variant="ghost"
            onClick={() => (window.location.href = '/')}
            data-testid="back-to-channel-button"
            className="back-button"
          >
            <Eye size={18} />
            Voir la chaîne
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-panel" data-testid="admin-panel">
      <div className="admin-header">
        <div className="header-content">
          <img
            src="https://customer-assets.emergentagent.com/job_bcc12bb2-d1c3-4c30-aab1-6d93b6ded326/artifacts/auol33u9_logo_4x.png"
            alt="Grace TV Gospel Logo"
            className="admin-logo"
          />
          <div>
            <h1>Grace TV Gospel — Panneau Admin</h1>
            <p>Gérez vos trois programmations</p>
          </div>
        </div>
        <div className="header-actions">
          <Button
            variant="outline"
            onClick={() => window.open('/', '_blank')}
            data-testid="preview-channel-button"
          >
            <Eye size={18} />
            Aperçu
          </Button>
          <Button
            variant="outline"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut size={18} />
            Déconnexion
          </Button>
        </div>
      </div>

      <div className="admin-content">
        <Tabs defaultValue="videos" className="admin-top-tabs">
          <TabsList className="admin-top-tabs-list" data-testid="admin-top-tabs-list">
            <TabsTrigger value="videos" data-testid="admin-tab-videos" className="admin-top-tab-trigger">
              <VideoIcon size={16} /> Vidéos
            </TabsTrigger>
            <TabsTrigger value="shows" data-testid="admin-tab-shows" className="admin-top-tab-trigger">
              <Tv size={16} /> Show TV
            </TabsTrigger>
            <TabsTrigger value="artists" data-testid="admin-tab-artists" className="admin-top-tab-trigger">
              <Users size={16} /> Artistes
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-tab-events" className="admin-top-tab-trigger">
              <CalendarHeart size={16} /> Événements
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings" className="admin-top-tab-trigger">
              <SettingsIcon size={16} /> Crédit & Contact
            </TabsTrigger>
          </TabsList>

          <TabsContent value="videos" data-testid="admin-tab-content-videos">
            <Card className="add-video-card">
          <h3>Ajouter une vidéo</h3>
          <form onSubmit={handleAddVideo} className="add-video-form">
            <Input
              placeholder="Titre de la vidéo"
              value={newVideo.title}
              onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
              disabled={isLoading}
              data-testid="video-title-input"
            />
            <Input
              placeholder="URL YouTube (ex : https://www.youtube.com/watch?v=...)"
              value={newVideo.youtube_url}
              onChange={(e) => setNewVideo({ ...newVideo, youtube_url: e.target.value })}
              disabled={isLoading}
              data-testid="video-url-input"
            />
            <div className="new-video-schedules">
              <span className="new-video-schedules-label">Diffuser dans :</span>
              {SLOTS.map((slot) => (
                <label
                  key={slot}
                  className="schedule-toggle"
                  data-testid={`new-video-schedule-${slot}`}
                >
                  <Checkbox
                    checked={newVideo.schedules.includes(slot)}
                    onCheckedChange={() => toggleNewVideoSchedule(slot)}
                  />
                  <span>{SLOT_LABELS[slot]}</span>
                </label>
              ))}
            </div>
            <Button type="submit" disabled={isLoading} data-testid="add-video-button">
              <Plus size={18} />
              Ajouter
            </Button>
          </form>
        </Card>

        <Card className="videos-list-card">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="schedule-tabs">
            <TabsList className="schedule-tabs-list" data-testid="schedule-tabs-list">
              {MAIN_TOP_SLOTS.map((slot) => (
                <TabsTrigger
                  key={slot}
                  value={slot}
                  data-testid={`tab-${slot}`}
                  className="schedule-tab-trigger"
                >
                  {TAB_ICON[slot]}
                  {slot === 'week'
                    ? `Semaine (${videosByTab.week.length + WEEKDAY_SLOTS.reduce((acc, d) => acc + videosByTab[d].length, 0)})`
                    : `${SLOT_LABELS[slot]} (${videosByTab[slot].length})`}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="week" data-testid="tab-content-week">
              <Tabs defaultValue="week" className="weekday-tabs">
                <TabsList className="weekday-tabs-list" data-testid="weekday-tabs-list">
                  <TabsTrigger value="week" data-testid="weekday-tab-week" className="weekday-tab-trigger">
                    Général ({videosByTab.week.length})
                  </TabsTrigger>
                  {WEEKDAY_SLOTS.map((slot) => (
                    <TabsTrigger
                      key={slot}
                      value={slot}
                      data-testid={`weekday-tab-${slot}`}
                      className="weekday-tab-trigger"
                    >
                      {SLOT_LABELS[slot]} ({videosByTab[slot].length})
                    </TabsTrigger>
                  ))}
                </TabsList>
                {['week', ...WEEKDAY_SLOTS].map((slot) => (
                  <TabsContent key={slot} value={slot} data-testid={`weekday-content-${slot}`}>
                    <div className="tab-content-header">
                      <h3>{TAB_DESCRIPTION[slot].title}</h3>
                      <p className="tab-subtitle">{TAB_DESCRIPTION[slot].subtitle}</p>
                    </div>
                    {renderVideoList(slot)}
                  </TabsContent>
                ))}
              </Tabs>
            </TabsContent>

            {['weekend', 'special'].map((slot) => (
              <TabsContent key={slot} value={slot} data-testid={`tab-content-${slot}`}>
                <div className="tab-content-header">
                  <h3>{TAB_DESCRIPTION[slot].title}</h3>
                  <p className="tab-subtitle">{TAB_DESCRIPTION[slot].subtitle}</p>
                </div>
                {renderVideoList(slot)}
              </TabsContent>
            ))}
          </Tabs>
        </Card>
          </TabsContent>

          <TabsContent value="shows" data-testid="admin-tab-content-shows">
            <ShowsManager token={token} />
          </TabsContent>

          <TabsContent value="artists" data-testid="admin-tab-content-artists">
            <ArtistsManager token={token} />
          </TabsContent>

          <TabsContent value="events" data-testid="admin-tab-content-events">
            <EventsManager token={token} />
          </TabsContent>

          <TabsContent value="settings" data-testid="admin-tab-content-settings">
            <SettingsManager token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
